import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../../../../app/config/axiosInstance';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import { buildQueries } from '@testing-library/react';
import { ViewDaySharp } from '@mui/icons-material';
import { deliveryTrackingURLs } from '../../../utils/deliveryTrackingURLs';

type ReturnedType = any;

type initialStateProps = {
  isLoading: boolean;
  data: any;
  error: string | null;
  count?: null;
};

const initialState: initialStateProps = {
  isLoading: false,
  data: [],
  error: null,
  count: null
};

// action for add dipatch preview
export const dispatchGetPreview = createAsyncThunk<ReturnedType, any>(
  'api/dispatchGetPreview',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${deliveryTrackingURLs?.GET_DISPATCH_PREVIEW}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for add dispatch preview
const dispatchGetPreviewSlice = createSlice({
  name: 'dispatchGetPreview',
  initialState: initialState,
  reducers: {
    clearPreviewData: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(dispatchGetPreview.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(dispatchGetPreview.fulfilled, (state, action: any) => {
      state.data = action.payload?.data;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(dispatchGetPreview.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for get load dropdown
export const getLoadsDropdown = createAsyncThunk<any, any>(
  'api/getLoadsDropdown',
  async (data, thunkApi) => {
    try {
      const { searchInput } = data;
      let url = `${deliveryTrackingURLs?.GET_LOADS_DROPDOWN}`;
      if (searchInput) {
        url += `?search=${searchInput}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for get load dropdown
const getLoadsDropdownSlice = createSlice({
  name: 'getLoadsDropDown',
  initialState: initialState,
  reducers: {
    clearLoadsDropdown: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(getLoadsDropdown.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getLoadsDropdown.fulfilled, (state, action: any) => {
      const { assignedLoadGetDetailsList, count } = action?.payload;
      state.isLoading = false;
      state.data = assignedLoadGetDetailsList?.map((item: any) => ({
        ...item,
        checked: false
      }));
      state.error = null;
      state.count = count;
    });
    builder.addCase(getLoadsDropdown.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for get truck dropdown
export const getTrucksDropdown = createAsyncThunk<any, any>(
  'api/getTrucksDropdown',
  async (data, thunkApi) => {
    try {
      const { datas, vehicleNumber } = data;
      let url = `${deliveryTrackingURLs?.GET_TRUCKS_DROPDOWN}`;
      if (vehicleNumber) {
        url += `?vehicleNumber=${vehicleNumber}`;
      }
      const response = await axiosInstance({
        method: 'POST',
        url: url,
        data: datas
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for get truck dropdown
const getTrucksDropdownSlice = createSlice({
  name: 'getTrucksDropdown',
  initialState: initialState,
  reducers: {
    clearTrucksDropdown: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(getTrucksDropdown.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getTrucksDropdown.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = action?.payload;
      state.error = null;
    });
    builder.addCase(getTrucksDropdown.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for get driver dropdown
export const getDriverDropdown = createAsyncThunk<any, any>(
  'api/getDriverDropdown',
  async (data, thunkApi) => {
    try {
      const { tripStart, tripEnd, vehicleNumber } = data;
      const response = await axiosInstance({
        method: 'GET',
        url: `${deliveryTrackingURLs?.GET_DRIVER}tripStart=${tripStart}&tripEnd=${tripEnd}&vehicleNumber=${vehicleNumber}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for get driver dropdown
const getDriverDropdownSlice = createSlice({
  name: 'getDriverDropdown',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getDriverDropdown.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getDriverDropdown.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = action?.payload;
      state.error = null;
    });
    builder.addCase(getDriverDropdown.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for add dipatch
export const addDeliveryDispatch = createAsyncThunk<ReturnedType, any>(
  'api/addDeliveryDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${deliveryTrackingURLs?.ADD_DELIVERY_DISPATCH}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for add dispatch preview
const addDeliveryDispatchSlice = createSlice({
  name: 'addDeliveryDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addDeliveryDispatch.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addDeliveryDispatch.fulfilled, (state, action: any) => {
      state.data = action.payload?.data;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addDeliveryDispatch.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for get all dispatch
export const getAllDispatch = createAsyncThunk<any, any>(
  'api/getAllDispatch',
  async (data, thunkApi) => {
    try {
      const {
        status,
        pageNo,
        pageSize,
        searchInput,
        vehicleNumber,
        driverName,
        startDate
      } = data;
      let url = `${deliveryTrackingURLs?.GET_ALL_DISPATCH}pageNo=${pageNo}&pageSize=${pageSize}&status=${status}`;
      if (searchInput) {
        url += `&searchInput=${searchInput}`;
      }
      if (vehicleNumber) {
        url += `&vehicleNumber=${vehicleNumber}`;
      }
      if (driverName) {
        url += `&driverName=${driverName}`;
      }
      if (startDate) {
        url += `&startDate=${startDate}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url
      });
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for get load dropdown
const getAllDispatchSlice = createSlice({
  name: 'getAllDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getAllDispatch.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getAllDispatch.fulfilled, (state, action: any) => {
      const { count, listAllTripsDetailsList } = action.payload;
      state.isLoading = false;
      state.data = listAllTripsDetailsList?.map((item: any) => ({
        ...item,
        orderStatus: item?.orderResponseList?.map((items: any) => items.orderStatus)
      }));
      state.error = null;
      state.count = count;
    });
    builder.addCase(getAllDispatch.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for view dispatch
export const viewDispatch = createAsyncThunk<any, any>(
  'api/viewDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${deliveryTrackingURLs?.VIEW_DISPATCH}dispatchId=${data}`
      });
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for view dispatch
const viewDispatchSlice = createSlice({
  name: 'viewDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(viewDispatch.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(viewDispatch.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(viewDispatch.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for delete dispatch
export const deleteDeliveryDispatch = createAsyncThunk<ReturnedType, any>(
  'api/deleteDeliveryDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${deliveryTrackingURLs?.DELETE_DISPATCH}dispatchId=${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//  slice for delete dispatch
const deleteDeliveryDispatchSlice = createSlice({
  name: 'deleteDeliveryDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deleteDeliveryDispatch.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteDeliveryDispatch.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = state.data.filter((row: any) => row.id !== action.payload.id);
      state.error = null;
    });
    builder.addCase(deleteDeliveryDispatch.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

//action for deactivate dispatch
export const deactivateDeliveryDispatch = createAsyncThunk<ReturnedType, any>(
  'api/deactivateDeliveryDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${deliveryTrackingURLs?.DEACTIVATE_DISPATCH}id=${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for deactivate dispatch
const deactivateDeliveryDispatchSlice = createSlice({
  name: 'deactivateDeliveryDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deactivateDeliveryDispatch.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deactivateDeliveryDispatch.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(deactivateDeliveryDispatch.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

// action for update dipatch
export const updateDeliveryDispatch = createAsyncThunk<ReturnedType, any>(
  'api/updateDeliveryDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${deliveryTrackingURLs?.UPDATE_DELIVERY_DISPATCH}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for update dispatch preview
const updateDeliveryDispatchSlice = createSlice({
  name: 'updateDeliveryDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateDeliveryDispatch.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(updateDeliveryDispatch.fulfilled, (state, action: any) => {
      state.data = action.payload?.data;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(updateDeliveryDispatch.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// action for get vehicle dropdown
export const getVehiclesDropdown = createAsyncThunk<any>(
  'api/getVehiclesDropdown',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${deliveryTrackingURLs?.GET_DELIVERY_VEHICLE}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for get load dropdown
const getVehiclesDropdownSlice = createSlice({
  name: 'getVehiclesDropdown',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getVehiclesDropdown.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getVehiclesDropdown.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = action?.payload;
    });
    builder.addCase(getVehiclesDropdown.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

export const dispatchGetPreviewReducer = dispatchGetPreviewSlice.reducer;
export const getLoadsDropdownReducer = getLoadsDropdownSlice.reducer;
export const getTrucksDropdownReducer = getTrucksDropdownSlice.reducer;
export const getDriverDropdownReducer = getDriverDropdownSlice.reducer;
export const addDeliveryDispatchReducer = addDeliveryDispatchSlice.reducer;
export const getAllDispatchReducer = getAllDispatchSlice.reducer;
export const viewDispatchReducer = viewDispatchSlice.reducer;
export const deleteDeliveryDispatchReducer = deleteDeliveryDispatchSlice.reducer;
export const deactivateDeliveryDispatchReducer = deactivateDeliveryDispatchSlice.reducer;
export const updateDeliveryDispatchReducer = updateDeliveryDispatchSlice.reducer;
export const getVehiclesDropdownReducer = getVehiclesDropdownSlice.reducer;
export const { clearLoadsDropdown } = getLoadsDropdownSlice.actions;
export const { clearPreviewData } = dispatchGetPreviewSlice.actions;
export const { clearTrucksDropdown } = getTrucksDropdownSlice.actions;
